'use client'

import { useTrackElementInteracted } from '@nwwp/segment-analytics'
import { cn } from '@nwwp/ui-shadcn'

type Short = {
  id: string
  title: string
  viewCount: string
  thumbnailUrl: string
}

function PlayIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5 4L20 12L5 20V4Z" />
    </svg>
  )
}

function ShuffleIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 3h5v5" />
      <path d="M4 20L21 3" />
      <path d="M21 16v5h-5" />
      <path d="M15 15l6 6" />
      <path d="M4 4l5 5" />
    </svg>
  )
}

function ShortsIcon() {
  return (
    <svg
      width="16"
      height="20"
      viewBox="0 0 15.9097 20.0251"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M1.80722 5.22195C0.0787003 6.21964 -0.513097 8.42923 0.48495 10.1573C1.16589 11.3373 2.41222 11.9876 3.68466 11.9636C3.78667 11.9617 3.84022 12.0996 3.75901 12.1614L2.28475 13.2827C0.556239 14.2804 -0.0355575 16.49 0.962489 18.218C1.95956 19.9461 4.16952 20.5381 5.89804 19.5404L14.1031 14.8031C15.8307 13.8055 16.4225 11.5959 15.4254 9.86783C14.7141 8.63636 13.3881 7.98179 12.0591 8.06834C11.9494 8.07548 11.8854 7.91235 11.9766 7.851L13.6256 6.74234C15.3531 5.74466 15.9449 3.53507 14.9478 1.80704C13.9498 0.0790104 11.7408 -0.513031 10.0123 0.48465L1.80722 5.22195Z"
        fill="#FF0000"
      />
      <path
        d="M10.6109 9.98885C10.7443 10.0658 10.7443 10.2583 10.6109 10.3353L6.24091 12.8583C6.10757 12.9353 5.94091 12.8391 5.94091 12.6851L5.94091 7.63902C5.94091 7.48506 6.10757 7.38883 6.24091 7.46581L10.6109 9.98885Z"
        fill="white"
      />
    </svg>
  )
}

/** Play all + Shuffle buttons — rendered inside the channel info card. */
export function YouTubeActionButtons({ channelUrl }: { channelUrl: string }) {
  const trackElementInteracted = useTrackElementInteracted()

  return (
    <div className="grid grid-cols-2 gap-3">
      <a
        href={`${channelUrl}/videos`}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'flex items-center justify-center gap-2 rounded-full',
          'bg-white px-4 py-2 text-body-lg text-charcoal hover:bg-frost'
        )}
        onClick={() => trackElementInteracted({ element: 'YouTube Play All' })}
      >
        <PlayIcon />
        Play all
      </a>
      <a
        href={channelUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'flex items-center justify-center gap-2 rounded-full',
          'bg-neutral-surface px-4 py-2 text-body-lg text-neutral-light hover:bg-divider'
        )}
        onClick={() => trackElementInteracted({ element: 'YouTube Shuffle' })}
      >
        <ShuffleIcon />
        Shuffle
      </a>
    </div>
  )
}

/** Shorts strip — rendered at the bottom of the channel card, outside the padded section. */
export function YouTubeShortsSection({ channelUrl, shorts }: { channelUrl: string; shorts: Short[] }) {
  const trackElementInteracted = useTrackElementInteracted()

  if (shorts.length === 0) return null

  return (
    <div className="relative border-t border-white/20 p-4 tablet:px-6">
      <div className="mb-4 flex items-center gap-2">
        <ShortsIcon />
        <p className="text-body-lg text-white">Shorts</p>
      </div>
      <div className="flex items-center justify-center gap-12 overflow-x-auto pb-2">
        {shorts.map((short, index) => (
          <a
            key={short.id || short.title || String(index)}
            href={short.id ? `https://www.youtube.com/shorts/${short.id}` : channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-36 shrink-0 flex-col gap-1"
            onClick={() => trackElementInteracted({ element: 'YouTube Shorts', videoId: short.id })}
          >
            <div className="relative aspect-[9/16] w-full overflow-hidden rounded-lg bg-neutral-surface">
              {short.thumbnailUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={short.thumbnailUrl} alt={short.title} className="absolute inset-0 size-full object-cover" />
              ) : (
                <div className="flex size-full items-center justify-center text-neutral-light">
                  <PlayIcon size={16} />
                </div>
              )}
            </div>
            <p className="line-clamp-2 text-body-sm text-white">{short.title}</p>
            <p className="text-body-xs text-neutral-light">{short.viewCount} views</p>
          </a>
        ))}
      </div>
    </div>
  )
}
